/**
 * 📊 Analytics Service - Сервис для управления аналитическими данными
 * 
 * Архитектурные принципы:
 * - Separation of Concerns (разделение ответственности)
 * - Observer Pattern (для уведомления подписчиков об изменениях)
 * - Single Responsibility (один класс = одна ответственность)
 * - Независимость от UI фреймворка
 */

import {
  getWorkspaces,
  getDailyActiveUsers,
  getActiveUsers,
  getAverageTime,
  getScenariosCount,
  getApplicationLaunchesRange,
  ApiError,
} from "@/lib/api-v3"
import { type AnalyticsPeriod, type PeriodSettings, type CustomDateRange } from "@/components/analytics-period-selector"
import { 
  getPeriodRange, 
  getPreviousPeriodRange, 
  getDayParameters, 
  getPreviousDayParameters,
  getPeriodDescription
} from "@/lib/period-utils"
import { getTokens, isAuthenticated, getUserData } from "@/lib/auth"
import { debugLog, debugError } from "@/lib/debug"

// 📊 Интерфейс данных аналитики
export interface AnalyticsData {
  // Уникальные пользователи за период (заменяет DAU)
  uniqueUsers: number | null
  // Запуски приложения за период (заменяет AU) 
  appLaunches: number | null
  // Среднее время в приложении (остается как есть)
  avgTime: number | null
  // Запуски процедур (остается как есть)
  scenarioLaunches: number | null
  // Предыдущие значения для расчета динамики
  prevUniqueUsers: number | null
  prevAppLaunches: number | null
  prevAvgTime: number | null
  prevScenarioLaunches: number | null
  // Метаинформация о периоде
  period: AnalyticsPeriod
  includeAdmins: boolean
  customRange?: CustomDateRange
  periodDescription: string
  isLoading: boolean
  error: string | null
  lastUpdated: Date | null
}

// 📡 Интерфейс наблюдателя (Observer Pattern)
export interface AnalyticsObserver {
  onDataChanged(data: AnalyticsData): void
}

/**
 * 🎯 Analytics Service - Основной класс для управления аналитикой
 */
export class AnalyticsService {
  private static instance: AnalyticsService
  private data: AnalyticsData
  private observers: Set<AnalyticsObserver> = new Set()
  private isLoading = false

  private constructor() {
    this.data = {
      uniqueUsers: null,
      appLaunches: null,
      avgTime: null,
      scenarioLaunches: null,
      prevUniqueUsers: null,
      prevAppLaunches: null,
      prevAvgTime: null,
      prevScenarioLaunches: null,
      period: 'month',
      includeAdmins: false,
      customRange: undefined,
      periodDescription: 'за месяц',
      isLoading: false,
      error: null,
      lastUpdated: null
    }
  }

  // 🔄 Singleton Pattern
  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService()
    }
    return AnalyticsService.instance
  }

  // 📡 Observer Pattern - подписка на изменения
  public subscribe(observer: AnalyticsObserver): () => void {
    this.observers.add(observer)
    
    // Возвращаем функцию для отписки
    return () => {
      this.observers.delete(observer)
    }
  }

  // 📢 Уведомление всех подписчиков об изменениях
  private notifyObservers(): void {
    this.observers.forEach(observer => {
      try {
        observer.onDataChanged({ ...this.data })
      } catch (error) {
        debugError('❌ Ошибка в observer:', error)
      }
    })
  }

  // 📄 Получение текущих данных
  public getData(): AnalyticsData {
    return { ...this.data }
  }

  // ⚡ Обновление состояния
  private updateData(updates: Partial<AnalyticsData>): void {
    this.data = {
      ...this.data,
      ...updates,
      lastUpdated: new Date()
    }
    this.notifyObservers()
  }

  // ⚙️ Обновление настроек периода
  public updatePeriodSettings(settings: PeriodSettings): void {
    this.data.period = settings.period
    this.data.includeAdmins = settings.includeAdmins
    this.data.customRange = settings.customRange
    this.data.periodDescription = getPeriodDescription(settings.period)
    this.notifyObservers()
  }

  // 🚀 Основной метод загрузки аналитики
  public async loadAnalytics(settings?: PeriodSettings): Promise<void> {
    // Предотвращаем множественные запросы
    if (this.isLoading) {
      debugLog('performance', '⚠️ Аналитика уже загружается, пропускаем запрос')
      return
    }

    this.isLoading = true
    
    // Применяем настройки, если переданы
    if (settings) {
      this.updatePeriodSettings(settings)
    }
    
    const period = this.data.period
    const includeAdmins = this.data.includeAdmins
    
    debugLog('performance', `📊 [AnalyticsService] Начинаем загрузку аналитики для периода "${period}" (включая админов: ${includeAdmins})`)

    try {
      // Устанавливаем состояние загрузки
      this.updateData({ isLoading: true, error: null })

      // 1. Проверяем авторизацию
      const authData = this.validateAuth()
      if (!authData) return

      const { accessToken, currentUser } = authData

      // 2. Workspace (в v3 = бывшая «группа» области данных)
      const workspaceId = await this.getFirstWorkspaceId(accessToken)
      if (workspaceId == null) return

      debugLog('performance', `📊 [AnalyticsService] Загружаем для workspace ${workspaceId}`)

      // 3. Подготавливаем параметры для нового периода
      const params = this.prepareNewAnalyticsParams(workspaceId, period, !includeAdmins)

      // 4. Параллельно загружаем все метрики
      const results = await this.fetchAllNewMetrics(accessToken, params)

      // 5. Обновляем данные
      this.updateData({
        uniqueUsers: results.uniqueUsers,
        prevUniqueUsers: results.prevUniqueUsers,
        appLaunches: results.appLaunches,
        prevAppLaunches: results.prevAppLaunches,
        avgTime: results.avgTime,
        prevAvgTime: results.prevAvgTime,
        scenarioLaunches: results.scenarioLaunches,
        prevScenarioLaunches: results.prevScenarioLaunches,
        isLoading: false,
        error: null
      })

      debugLog('performance', '✅ [AnalyticsService] Аналитика успешно загружена')

    } catch (error) {
      debugError('💥 [AnalyticsService] Ошибка загрузки:', error)
      
      const errorMessage = this.getErrorMessage(error)
      this.updateData({
        isLoading: false,
        error: errorMessage
      })
    } finally {
      this.isLoading = false
    }
  }

  // 🔐 Валидация авторизации
  private validateAuth(): { accessToken: string; currentUser: any } | null {
    if (!isAuthenticated()) {
      this.updateData({ isLoading: false, error: "Пользователь не авторизован" })
      return null
    }

    const tokens = getTokens()
    if (!tokens?.accessToken) {
      this.updateData({ isLoading: false, error: "Токен доступа не найден" })
      return null
    }

    const currentUser = getUserData()
    if (!currentUser) {
      this.updateData({ isLoading: false, error: "Данные пользователя не найдены" })
      return null
    }

    return { accessToken: tokens.accessToken, currentUser }
  }

  /** Первый workspace пользователя (аналог старой «группы» для метрик). */
  private async getFirstWorkspaceId(accessToken: string): Promise<number | null> {
    const list = await getWorkspaces(accessToken)
    if (!list || list.length === 0) {
      this.updateData({ isLoading: false, error: "У пользователя нет доступных workspace" })
      return null
    }
    const wid = list[0].workspaceId
    if (wid == null) {
      this.updateData({ isLoading: false, error: "Не удалось определить workspace" })
      return null
    }
    return wid
  }

  // ⚙️ Подготовка параметров для новых запросов
  private prepareNewAnalyticsParams(workspaceId: number, period: AnalyticsPeriod, ignoreAdmins: boolean) {
    const currentRange = getPeriodRange(period, this.data.customRange)
    const prevRange = getPreviousPeriodRange(period, this.data.customRange)
    
    // Для DAU нужны параметры конкретного дня
    const dayParams = getDayParameters(period, this.data.customRange)
    const prevDayParams = getPreviousDayParameters(period, this.data.customRange)

    return {
      workspaceId,
      ignoreAdmins,
      period,
      // Текущий период
      currentStartTime: currentRange.startTimestamp,
      currentEndTime: currentRange.endTimestamp,
      // Предыдущий период
      prevStartTime: prevRange.startTimestamp,
      prevEndTime: prevRange.endTimestamp,
      // Параметры для DAU (если применимо)
      dayParams,
      prevDayParams
    }
  }

  // 🔄 Параллельная загрузка всех новых метрик
  private async fetchAllNewMetrics(accessToken: string, params: any) {
    const promises: Promise<any>[] = []
    
    // 1. Уникальные пользователи за период
    if (params.period === 'today' || params.period === 'yesterday') {
      // Для конкретных дней используем DAU
      if (params.dayParams) {
        promises.push(
          getDailyActiveUsers(accessToken, {
            workspace: params.workspaceId,
            year: params.dayParams.year,
            month: params.dayParams.month,
            day: params.dayParams.day,
            ignoreAdmins: params.ignoreAdmins
          })
        )
        
        if (params.prevDayParams) {
          promises.push(
            getDailyActiveUsers(accessToken, {
              workspace: params.workspaceId,
              year: params.prevDayParams.year,
              month: params.prevDayParams.month,
              day: params.prevDayParams.day,
              ignoreAdmins: params.ignoreAdmins
            })
          )
        } else {
          promises.push(Promise.resolve(null))
        }
      } else {
        promises.push(Promise.resolve(null))
        promises.push(Promise.resolve(null))
      }
    } else {
      // Для периодов используем AU (активные пользователи)
      promises.push(
        getActiveUsers(accessToken, {
          workspace: params.workspaceId,
          from: params.currentStartTime,
          to: params.currentEndTime,
          ignoreAdmins: params.ignoreAdmins
        })
      )
      promises.push(
        getActiveUsers(accessToken, {
          workspace: params.workspaceId,
          from: params.prevStartTime,
          to: params.prevEndTime,
          ignoreAdmins: params.ignoreAdmins
        })
      )
    }
    
    // 2. Запуски приложения за период (сумма значений из range API)
    promises.push(
      getApplicationLaunchesRange(accessToken, {
        workspace: params.workspaceId,
        begin: params.currentStartTime,
        end: params.currentEndTime,
        ignoreAdmins: params.ignoreAdmins
      }).then((ranges: number[]) => ranges.reduce((sum, count) => sum + count, 0))
    )
    promises.push(
      getApplicationLaunchesRange(accessToken, {
        workspace: params.workspaceId,
        begin: params.prevStartTime,
        end: params.prevEndTime,
        ignoreAdmins: params.ignoreAdmins
      }).then((ranges: number[]) => ranges.reduce((sum, count) => sum + count, 0))
    )
    
    // 3. Среднее время в приложении
    promises.push(
      getAverageTime(accessToken, {
        workspace: params.workspaceId,
        begin: params.currentStartTime,
        end: params.currentEndTime,
        ignoreAdmins: params.ignoreAdmins
      })
    )
    promises.push(
      getAverageTime(accessToken, {
        workspace: params.workspaceId,
        begin: params.prevStartTime,
        end: params.prevEndTime,
        ignoreAdmins: params.ignoreAdmins
      })
    )
    
    // 4. Запуски процедур
    promises.push(
      getScenariosCount(accessToken, {
        workspace: params.workspaceId,
        begin: params.currentStartTime,
        end: params.currentEndTime,
        ignoreAdmins: params.ignoreAdmins
      })
    )
    promises.push(
      getScenariosCount(accessToken, {
        workspace: params.workspaceId,
        begin: params.prevStartTime,
        end: params.prevEndTime,
        ignoreAdmins: params.ignoreAdmins
      })
    )

    const results = await Promise.allSettled(promises)

    // Логируем ошибки отдельных запросов
    const metricNames = [
      'Уникальные пользователи', 'Уникальные пользователи (пред.)',
      'Запуски приложения', 'Запуски приложения (пред.)', 
      'Среднее время', 'Среднее время (пред.)',
      'Процедуры', 'Процедуры (пред.)'
    ]
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        debugError(`❌ [AnalyticsService] Ошибка ${metricNames[index]}:`, result.reason)
      }
    })

    return {
      uniqueUsers: results[0].status === 'fulfilled' ? results[0].value : null,
      prevUniqueUsers: results[1].status === 'fulfilled' ? results[1].value : null,
      appLaunches: results[2].status === 'fulfilled' ? results[2].value : null,
      prevAppLaunches: results[3].status === 'fulfilled' ? results[3].value : null,
      avgTime: results[4].status === 'fulfilled' ? results[4].value : null,
      prevAvgTime: results[5].status === 'fulfilled' ? results[5].value : null,
      scenarioLaunches: results[6].status === 'fulfilled' ? results[6].value : null,
      prevScenarioLaunches: results[7].status === 'fulfilled' ? results[7].value : null
    }
  }


  // 🚨 Обработка ошибок
  private getErrorMessage(error: unknown): string {
    if (error instanceof ApiError) {
      return `API ошибка ${error.status}: ${error.message}`
    }
    if (error instanceof Error) {
      return error.message
    }
    return "Неизвестная ошибка"
  }

  // 🔄 Принудительное обновление данных
  public async refresh(): Promise<void> {
    debugLog('performance', '🔄 [AnalyticsService] Принудительное обновление')
    await this.loadAnalytics()
  }

  // 🧹 Очистка данных
  public clear(): void {
    debugLog('performance', '🧹 [AnalyticsService] Очистка данных')
    this.data = {
      uniqueUsers: null,
      appLaunches: null,
      avgTime: null,
      scenarioLaunches: null,
      prevUniqueUsers: null,
      prevAppLaunches: null,
      prevAvgTime: null,
      prevScenarioLaunches: null,
      period: 'month',
      includeAdmins: false,
      customRange: undefined,
      periodDescription: 'за месяц',
      isLoading: false,
      error: null,
      lastUpdated: null
    }
    this.notifyObservers()
  }

  // 📊 Получение статистики сервиса
  public getStats() {
    return {
      observersCount: this.observers.size,
      isLoading: this.isLoading,
      lastUpdated: this.data.lastUpdated,
      hasData: this.data.uniqueUsers !== null || this.data.appLaunches !== null || this.data.avgTime !== null || this.data.scenarioLaunches !== null,
      period: this.data.period,
      includeAdmins: this.data.includeAdmins
    }
  }
}

// 🎯 Экспортируем синглтон для использования
export const analyticsService = AnalyticsService.getInstance()

// 🪝 React Hook для использования сервиса
export function useAnalytics() {
  return analyticsService
} 